import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { NgClass } from '@angular/common';
import { ApiService } from '../../../../core/services/api.service';
import { API_ROUTES } from '../../../../core/constants/api-routes';
import {
  CreatorPromptDetail,
  CreatorPromptHistoryItem,
} from '../../../../core/models/schedule-prompt.models';

@Component({
  selector: 'app-creator-view',
  imports: [RouterLink, NgClass],
  templateUrl: './creator-view.html',
  styleUrl: './creator-view.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreatorView implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(ApiService);

  // ── Route params ──────────────────────────────────────────────────────────
  readonly schedulePromptId = signal<string | null>(null);
  readonly memberId = signal<string | null>(null);

  // ── UI state ──────────────────────────────────────────────────────────────
  readonly hasError = signal(false);

  // ── Server data ───────────────────────────────────────────────────────────
  readonly detail = signal<CreatorPromptDetail | null>(null);

  // ── Active history filter ─────────────────────────────────────────────────
  readonly activeFilter = signal<'all' | 'responded' | 'not-responded' | 'waiting'>('all');

  // ─────────────────────────────────────────────────────────────────────────
  // Lifecycle
  // ─────────────────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.schedulePromptId.set(this.route.snapshot.paramMap.get('schedulePromptId'));
    this.memberId.set(this.route.snapshot.paramMap.get('memberId'));
    this.loadDetail();
  }

  // ─────────────────────────────────────────────────────────────────────────
  // API
  // ─────────────────────────────────────────────────────────────────────────

  async loadDetail(): Promise<void> {
    const promptId = this.schedulePromptId();
    const memId = this.memberId();
    if (!promptId || !memId) return;

    this.hasError.set(false);

    try {
      const resp = await this.api.request<CreatorPromptDetail>(
        'GET',
        `${API_ROUTES.SP_GET_CREATOR_DETAIL}/${promptId}/${memId}`,
        undefined,
        { showToaster: false, showLoader: true, useToken: true },
      );

      if (resp.status && resp.data) {
        this.detail.set(resp.data);
      } else {
        this.hasError.set(true);
      }
    } catch {
      this.hasError.set(true);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Template helpers
  // ─────────────────────────────────────────────────────────────────────────

  /** Returns the CSS item-modifier class based on responseStatus string. */
  getHistoryItemClass(status: string): string {
    switch (status?.toLowerCase()) {
      case 'responded':
        return 'fm-responded';
      case 'notresponded':
      case 'noresponse':
      case 'no response':
      case 'no response received':
        return 'fm-not-responded';
      case 'awaitingresponse':
      case 'awaiting response':
      case 'waiting':
        return 'fm-waiting';
      default:
        return 'fm-waiting';
    }
  }

  /** Returns the CSS badge class based on responseStatus string. */
  getStatusBadgeClass(status: string): string {
    switch (status?.toLowerCase()) {
      case 'responded':
        return 'fm-status-success';
      case 'notresponded':
      case 'noresponse':
      case 'no response':
      case 'no response received':
        return 'fm-status-danger';
      default:
        return 'fm-status-warning';
    }
  }

  /** Returns the Bootstrap icon class for the timeline dot. */
  getTimelineIcon(status: string): string {
    switch (status?.toLowerCase()) {
      case 'responded':
        return 'bi-check-lg';
      case 'notresponded':
      case 'noresponse':
      case 'no response':
      case 'no response received':
        return 'bi-x-lg';
      default:
        return 'bi-hourglass-split';
    }
  }

  /** Returns the human-readable status label. */
  getStatusLabel(status: string): string {
    switch (status?.toLowerCase()) {
      case 'responded':
        return 'Responded';
      case 'notresponded':
      case 'noresponse':
      case 'no response':
      case 'no response received':
        return 'No Response Received';
      default:
        return 'Awaiting Response';
    }
  }

  /** True only when the member has responded (show View Story button). */
  isResponded(status: string): boolean {
    return status?.toLowerCase() === 'responded';
  }

  /** True only when the cycle passed with no response (show nothing extra). */
  isNoResponse(status: string): boolean {
    const s = status?.toLowerCase();
    return s === 'notresponded' || s === 'noresponse' || s === 'no response' || s === 'no response received';
  }

  /** Returns history items filtered by the active tab. */
  filteredHistory(): CreatorPromptHistoryItem[] {
    const all = this.detail()?.promptHistory ?? [];
    const filter = this.activeFilter();
    if (filter === 'all') return all;
    return all.filter((h) => this.getHistoryItemClass(h.responseStatus) === `fm-${filter}`);
  }

  setFilter(filter: 'all' | 'responded' | 'not-responded' | 'waiting'): void {
    this.activeFilter.set(filter);
  }

  formatDate(dateStr: string | null | undefined): string {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return isNaN(d.getTime())
      ? ''
      : d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  }
}
